import { useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import { useNavigate } from 'react-router-dom';
import { platform } from '@/lib/platform';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export function useDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!platform.isNative) return;

    const handleDeepLink = async (incomingUrl: string) => {
      try {
        // incomingUrl can be:
        // https://app.forgehq.io/finance/contributions/123
        // or com.glorifyllc.forge://callback?code=xxx (OAuth)
        const url = new URL(incomingUrl);
        const oauthCode = url.searchParams.get('code');

        if (oauthCode) {
          const { error } = await supabase.auth.exchangeCodeForSession(oauthCode);
          if (error) {
            logger.warn('[DeepLinks] Failed to exchange OAuth code from native callback', {
              error: logger.sanitizeForUser(error.message),
            });
          }
          return;
        }

        const path = url.pathname;
        if (path && path !== '/') {
          navigate(path);
        }
      } catch (error) {
        logger.warn('[DeepLinks] Failed to parse incoming deep link URL', {
          error: logger.sanitizeForUser(error instanceof Error ? error.message : 'invalid_deeplink_url'),
        });
      }
    };

    const listener = CapApp.addListener('appUrlOpen', (event) => {
      void handleDeepLink(event.url);
    });

    // Handle app launched from terminated state with URL
    CapApp.getLaunchUrl().then(result => {
      if (result?.url) {
        void handleDeepLink(result.url);
      }
    });

    return () => {
      listener.then(l => l.remove());
    };
  }, [navigate]);
}
